<template>
  <v-list-item style="max-width: 400px; position: relative">
    <v-list-item-content>
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex" @click="viewNotification">
          <div class="mr-3">
            <v-avatar size="55">
              <base-image
                type="u"
                image-type="t"
                :file-name="notification.origin.logo"
                :profile-id="notification.origin.profileId.replace('#', '')"
                :isProfileImage="true"
              ></base-image>
            </v-avatar>
          </div>
          <div>
            <h4 style="font-size: 13px; margin-bottom: 5px">
              {{ title }}
            </h4>
            <p class="title-">
              <i>{{ subtitle }}</i>
            </p>
            <p class="subtitle" style="max-width: 220px">
              {{ subSubTitle }}
            </p>
            <p class="subtitle brand--text">
              {{ toCalendarDate(notification.notice.creationDate) }}
            </p>
          </div>
        </div>

        <div
          style="display: flex; flex-direction: column; justify-content: center; align-items: center"
        >
          <div class="mb-5">
            <v-btn v-if="isRead" fab depressed small @click="markAsRead(false)">
              <v-icon>{{ icons.mdiEmailOpen }}</v-icon>
            </v-btn>
            <v-btn v-else fab depressed small @click="markAsRead(true)">
              <v-icon color="brand">{{ icons.mdiEmail }}</v-icon>
            </v-btn>
          </div>
          <div>
            <v-btn @click="$emit('delete-notification')" fab depressed small>
              <v-icon color="red">{{ icons.mdiDelete }}</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </v-list-item-content>
  </v-list-item>
</template>

<script>
import { mapState } from 'vuex';
import { MomentMixin } from '@/utilities/mixins/MomentMixin.js';
import BaseImage from './base_components/BaseImage/BaseImage.vue';
import { mdiEmail, mdiEmailOpen, mdiDelete } from '@mdi/js';
import { notices } from '@/utilities/api';

export default {
  name: 'NotificationCard.vue',

  components: { BaseImage },

  props: {
    notification: {
      type: Object,
      required: true
    },

    deleteLoading: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    readLoading: false,
    notificationData: {}
  }),

  async created() {
    this.notificationData = { ...this.notification };
  },

  mixins: [MomentMixin],

  methods: {
    toCalendarDate(dateString) {
      return this.momentDateFromNow(dateString);
    },

    setVueI18Label(string) {
      return this.$t(string.toLowerCase()).toUpperCase();
    },

    async markAsRead(onlyRead) {
      const id = this.notification.notice.noticeId.replace('#', '');
      this.readLoading = true;
      await notices.updateStatus(id, {
        markAsRead: onlyRead
      });
      this.notificationData.notice.markAsRead = onlyRead;

      this.readLoading = false;
      this.$emit(
        'markedAsReadCounter',
        this.notificationData.notice.markAsRead ? -1 : 1
      );
    },

    async viewNotification() {
      await this.markAsRead(true);
      const { type, data } = this.notification.notice;
      if (
        type === 'DOCUMENT_STATUS_CHANGE' ||
        type === 'DOCUMENT_APPROVAL' ||
        type === 'REVISION_UPDATE'
      ) {
        this.routeTo();
        return;
      } else if (
        type === 'PROJECT_HANDLE_INVITE' ||
        type === 'PROJECT_INVITE'
      ) {
        this.$emit('show-invite-details');
      } else if (type === 'ROLE_REQUEST') {
        this.$emit('show-role-request-details');
      } else if (type === 'MEMBER_REASSIGNMENT') {
        this.$router.push({
          name: 'viewSelectedProjectGroup',
          params: {
            id: data[0].projectId.replace('#', ''),
            groupId: data[0].projectGroupId.replace('#', '')
          }
        });
      } else if (type === 'ROLE_UPDATE') {
        this.$router.push({
          name: 'viewSelectedProject',
          params: {
            id: data[0].projectId.replace('#', ''),
            lang: this.defaultLanguage
          }
        });
      }
    },

    routeTo() {
      const documentId = this.notification.notice.data[0].documentId.replace(
        '#',
        ''
      );

      this.$router.push({
        name: 'browseRevisions',
        query: {
          id: documentId
        }
      });
    }
  },

  computed: {
    ...mapState({
      defaultLanguage: state => state.defaultLanguage
    }),

    notifData() {
      return this.notification.notice.data[0];
    },

    title() {
      const { type } = this.notification.notice;
      if (type === 'DOCUMENT_STATUS_CHANGE' || type === 'DOCUMENT_APPROVAL') {
        if (type === 'DOCUMENT_APPROVAL') {
          return `${this.$t('documentApproved')} - ${
            this.notifData.documentName
          }`;
        }
        return `${this.setVueI18Label(this.notifData.documentStatus)} - ${
          this.notifData.documentName
        }`;
      } else if (type === 'REVISION_UPDATE') {
        return `${this.$t('revisionUpdate')} - ${this.notifData.documentName}`;
      } else if (type === 'ROLE_REQUEST') {
        return `${this.notifData.profileNameFrom} ${this.$t('roleRequest')}: ${
          this.notifData.suggestion.role
        }`;
      } else if (type === 'PROJECT_HANDLE_INVITE') {
        return `${this.$t('sentInvitationTo')} ${this.notifData.projectName}`;
      } else if (type === 'PROJECT_INVITE') {
        return `${this.$t('beenInvitedJoin')} ${this.notifData.projectName}`;
      } else if (type === 'MEMBER_REASSIGNMENT') {
        return this.$t('youHaveBeenReassigned');
      } else if (type === 'ROLE_UPDATE') {
        return `${this.$t('yourRoleHasBeenUpdated')}:`;
      } else if (type === 'MEMBER_REMOVAL') {
        return `${this.notifData.profileNameFrom} ${this.$t('leftProject')}`;
      }
      return '';
    },

    subtitle() {
      return this.notifData.projectName;
    },

    subSubTitle() {
      const { type } = this.notification.notice;
      if (type === 'DOCUMENT_STATUS_CHANGE' || type === 'DOCUMENT_APPROVAL') {
        return `${this.notifData.documentName} ${this.$t(
          'hasMovedTo'
        )} ${this.setVueI18Label(this.notifData.documentStatus)}`;
      } else if (type === 'REVISION_UPDATE') {
        return `${this.notifData.profileNameFrom} ${this.$t(
          'hasMadeChangeTo'
        )} ${this.notifData.documentName}`;
      } else if (type === 'ROLE_REQUEST') {
        return `${this.notifData.suggestion.message}`;
      } else if (type === 'PROJECT_HANDLE_INVITE') {
        return `${this.$t('to')}: ${this.notifData.profileNameFrom}`;
      } else if (type === 'PROJECT_INVITE') {
        return `${this.$t('from')}: ${this.notifData.profileNameFrom}`;
      } else if (type === 'MEMBER_REASSIGNMENT') {
        return `${this.notifData.profileNameFrom} ${this.$t(
          'reassignedToPG'
        )} ${this.notifData.projectGroupName}`;
      }
      return '';
    },

    isRead() {
      return this.notification.notice.markAsRead;
    },

    icons: () => ({
      mdiEmail,
      mdiEmailOpen,
      mdiDelete
    })
  }
};
</script>

<style scoped>
.title- {
  font-size: 12px;
  margin: 0;
  padding: 0;
  margin-bottom: 4px;
}

.subtitle {
  font-size: 12px;
  margin: 0;
  padding: 0;
  margin-bottom: 4px;
}

.list-item-hover {
  transition: 0.4s all linear;
  cursor: pointer;
}

.list-item-hover:hover {
  background: #ddd;
}
</style>
